/**
 * Bereinigung von Nutzer-CSS (eigenes CSS, Theme-Variablen). Ziel: kein Nachladen fremder Stylesheets,
 * keine Skript-URLs, keine Legacy-Skript-Konstrukte, kein Ausbruch aus dem <style>-Element.
 *
 * Vorgehen:
 *  1. Kommentare entfernen (verhindert Verschleierung wie `@im/ ** /port`).
 *  2. CSS-Escapes normalisieren (`@\69mport` → `@import`); Escapes, die Bedeutung tragen (Anführungszeichen,
 *     Backslash, Zeilenumbruch, `<`), bleiben als Hex-Escape erhalten.
 *  3. Gefährliche At-Regeln (`@import`, `@charset`, `@namespace`, `@-moz-document`) entfernen.
 *     Übrig gebliebene Schlüsselwörter (z. B. `{` im Import-String) werden zu einer unbekannten At-Regel
 *     umbenannt – der Browser verwirft sie samt Inhalt.
 *  4. `url()`/`src()`/`image-set()`-Ziele nur erlauben, wenn eigene Medien (/api/media/) oder
 *     data:-Rasterbilder (passend zur CSP); sonst durch `none` ersetzen.
 *  5. `expression(`, `javascript:`, `vbscript:`, `-moz-binding`, `behavior:` neutralisieren.
 *  6. `</style`, `<!--`, `-->` entfernen.
 */

const KEEP_ESCAPED = new Set(['"', "'", '\\', '\n', '\r', '\f', '<', '>']);

export function normalizeEscapes(css: string): string {
  return css.replace(/\\([0-9a-fA-F]{1,6})[ \t\n\r\f]?|\\([^\n0-9a-fA-F])/g, (m, hex: string | undefined, ch: string | undefined) => {
    let c: string;
    if (hex !== undefined) {
      const cp = parseInt(hex, 16);
      if (cp === 0 || cp > 0x10ffff || (cp >= 0xd800 && cp <= 0xdfff)) return '�';
      c = String.fromCodePoint(cp);
    } else {
      c = ch!;
    }
    if (KEEP_ESCAPED.has(c) || c.charCodeAt(0) < 0x20) return `\\${c.codePointAt(0)!.toString(16)} `;
    return c;
  });
}

const SAFE_DATA_IMAGE = /^data:image\/(png|jpe?g|gif|webp|avif);base64,[a-z0-9+/=\s]+$/i;

export function isSafeCssUrl(raw: string): boolean {
  const url = raw.trim().replace(/^['"]|['"]$/g, '').trim();
  if (!url) return false;
  if (url.startsWith('/api/media/')) return !/[\s'"()\\]|\.\./.test(url);
  return SAFE_DATA_IMAGE.test(url);
}

/** Ersetzt unsichere url()/src()-Funktionen. */
function filterUrls(css: string): string {
  return css.replace(/\b(url|src)\s*\(\s*((?:"[^"]*"|'[^']*'|[^)]*))\s*\)/gi, (m, _fn: string, arg: string) => (isSafeCssUrl(arg) ? m : 'none'));
}

export function sanitizeCss(input: string | null | undefined): string {
  if (!input) return '';
  // Bis zum Fixpunkt wiederholen: Entfernen darf keine neuen gefährlichen Konstrukte zusammenfügen
  // (`@imp@import x;ort …` → `@import …`, `</sty</stylele>` → `</style>`).
  let css = input;
  for (let i = 0; i < 10; i++) {
    const next = sanitizeOnce(css);
    if (next === css) return css;
    css = next;
  }
  // Konvergiert nicht → im Zweifel nichts übernehmen.
  return '';
}

function sanitizeOnce(input: string): string {
  let css = input.replace(/\/\*[\s\S]*?(\*\/|$)/g, '');
  css = normalizeEscapes(css);
  // Nach dem Normalisieren erneut Kommentare (aus Escapes entstanden) entfernen.
  css = css.replace(/\/\*[\s\S]*?(\*\/|$)/g, '');
  css = css.replace(/<\/?style/gi, '').replace(/<!--|-->/g, '');
  css = css.replace(/@(import|charset|namespace)\b[^;{}]*(;|$)/gi, '');
  css = css.replace(/@(import|charset|namespace)\b/gi, '@lf-blocked');
  // Block-At-Regeln ohne Nutzen, aber mit Risiko: Inhalt komplett verwerfen.
  css = css.replace(/@-moz-document\b[^{]*\{(?:[^{}]*\{[^{}]*\})*[^{}]*\}/gi, '');
  css = css.replace(/@-moz-document\b/gi, '');
  css = filterUrls(css);
  css = css.replace(/\bimage-set\s*\(/gi, 'none(');
  css = css.replace(/expression\s*\(/gi, 'none(');
  css = css.replace(/(java|vb)script\s*:/gi, '');
  css = css.replace(/-moz-binding\s*:[^;}]*/gi, '');
  css = css.replace(/\bbehavior\s*:[^;}]*/gi, '');
  return css;
}

/** Erlaubte CSS-Variablen (Theme-Überschreibungen). Name `--lf-*`, Wert ohne Block-/Regelzeichen. */
export function sanitizeThemeVariables(vars: Record<string, string> | null | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  if (!vars) return out;
  for (const [k, v] of Object.entries(vars)) {
    if (!/^--lf-[a-z0-9-]{1,48}$/.test(k) || typeof v !== 'string' || v.length > 400) continue;
    const clean = sanitizeCss(v).trim();
    // Alles, was der Sanitizer verändern musste, ist verdächtig → verwerfen statt halb reparieren.
    if (!clean || clean !== v.trim() || /[;{}<>\\]/.test(clean)) continue;
    out[k] = clean;
  }
  return out;
}

/** CSS-Text für `:root { --lf-…: … }`. */
export function themeVariablesCss(vars: Record<string, string>): string {
  const lines = Object.entries(vars).map(([k, v]) => `  ${k}: ${v};`);
  return lines.length ? `:root, :root[data-theme] {\n${lines.join('\n')}\n}` : '';
}
